import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { storedLocalStorageData, storeLocalStorageData } from '../util/data';
import moment from 'moment';
import { apiBaseUrl } from '../util/api';
import axios from 'axios';
import { storableError } from '../util/errors';

const TRANSLATION_KEY = 'translation_content';
const EXPIRY_LIMIT = parseInt(process.env.REACT_APP_TRANSLATION_EXPIRE_LIMIT || '2', 10); // "Cache upto 2 hours"

//====================== Helpers=====================//
export const getSavedTranslations = () => {
  try {
    const translations = storedLocalStorageData(TRANSLATION_KEY);
    return translations;
  } catch (err) {
    return {};
  }
};

const saveTranslations = data => {
  try {
    storeLocalStorageData(TRANSLATION_KEY, data);
  } catch (err) {}
};

const isDataExpired = savedData => {
  if (typeof savedData !== 'object' || savedData == null) {
    return true;
  }
  const { expiresIn } = savedData;
  if (typeof expiresIn !== 'number') {
    return true;
  }
  const diff = moment(expiresIn).diff(moment(), 'hour');
  const isExpired = diff > EXPIRY_LIMIT || diff < 0; // moment().isAfter(moment(expiresIn));
  return isExpired;
};

const merge = (prevTotalTranslations, batchTranslation) => {
  const {
    ref: { lang, batchId },
    ...rest
  } = batchTranslation;
  prevTotalTranslations = { ...prevTotalTranslations };
  if (prevTotalTranslations[lang] === undefined) {
    prevTotalTranslations[lang] = {};
  }
  if (prevTotalTranslations[lang][batchId] === undefined) {
    prevTotalTranslations[lang][batchId] = {};
  }
  prevTotalTranslations[lang][batchId] = rest;
  return prevTotalTranslations;
};

const hasDataSetChanged = (oldData, newData) => {
  const keys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);
  for (const key of keys) {
    if (oldData[key] !== newData[key]) {
      return true;
    }
  }
  return false;
};

//====================================== Async Thunk=========================//
//{[lang]:{[batchId]:{translation:object,expiresIn:date}}}}
const translationCreator = async ({ lang, batchId, data }, { getState, rejectWithValue }) => {
  try {
    const { translations } = getState().translation;
    const saveData = translations[lang]?.[batchId];
    const enSavedData = translations['en']?.[batchId] || {}; // Assuming base message in english
    const shouldCallTranslateAPI =
      isDataExpired(saveData) || hasDataSetChanged(enSavedData?.translation || {}, data);
    if (!shouldCallTranslateAPI) {
      return { ...saveData, ref: { lang, batchId } };
    }
    const expiresIn = moment()
      .add(EXPIRY_LIMIT, 'hours')
      .valueOf();
    const url = `${apiBaseUrl()}/api/batch/${batchId}/translate-content`;
    const resp = await axios.post(url, { lang, data, expiresIn });
    const translationData = resp.data;
    return { translation: translationData, expiresIn, ref: { lang, batchId } };
  } catch (err) {
    return rejectWithValue(storableError(err));
  }
};

export const translateThunk = createAsyncThunk('translation/translate', translationCreator);

export const translate = ({ lang, batchId, data }) => dispatch => {
  return dispatch(translateThunk({ lang, batchId, data }));
};

//=========================== Slice ====================//
const initialState = {
  translations: getSavedTranslations(),
  translationInProgress: false,
  translationErr: null,
};

const translationSlice = createSlice({
  name: 'translationSlice',
  initialState,
  reducers: {
    initiateTranslation(state, action) {
      state.translations = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(translateThunk.pending, state => {
        state.translationInProgress = true;
        state.translationErr = null;
      })
      .addCase(translateThunk.fulfilled, (state, action) => {
        const translations = merge(state.translations, action.payload);
        state.translationInProgress = false;
        saveTranslations(translations);
        state.translations = translations;
      })
      .addCase(translateThunk.rejected, (state, action) => {
        state.translationInProgress = false;
        state.translationErr = action.payload;
      });
  },
});

export default translationSlice.reducer;
export const { initiateTranslation } = translationSlice.actions;
